import tenantData from '@data';
import tenantTheme from '@theme';
import tenantUtils from '@utils';
import { Typography } from 'antd';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getUnifiedListingTierProductTagProps } from '../../../tenant/common/utils/unifiedListingTierBadge';
import { getBaseURL } from '../../../utility/env';
import { Flex, Image, Number as PriceNumber, ProductTag, Tag, TextWithIcon } from '../../common';
import { HealthTag } from '../../listing-health/styled';
import { coverImageInBox } from './assignee-radio-grid';

export const CHANGE_OWNER_CARD_MAX_WIDTH = 420;

/** Card shell used by the Change Listing Ownership summaries (listing + assignee). */
export const changeOwnerSummaryCardStyle = {
  background: 'linear-gradient(0deg, #F5F5F5 0%, #FFFFFF 100%)',
  borderRadius: 10,
  padding: 12,
  boxSizing: 'border-box',
  width: '100%',
  maxWidth: CHANGE_OWNER_CARD_MAX_WIDTH,
  minHeight: 108,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
};

const listingHealthScoreTagColor = (classification) => {
  switch (classification) {
    case 'high':
      return 'success';
    case 'low':
      return 'error';
    case 'medium':
      return 'warning';
    default:
      return 'warning';
  }
};

const getAppliedProductSlug = (productsInfo) => {
  if (!productsInfo || typeof productsInfo !== 'object') return null;
  if (productsInfo['signature-listing']?.is_applied) return 'signature-listing';
  if (productsInfo['hot-listing']?.is_applied) return 'hot-listing';
  if (productsInfo['basic-listing-dubizzle']?.is_applied) return 'basic-listing-dubizzle';
  if (productsInfo['basic-listing']?.is_applied) return 'basic-listing';
  return null;
};

/**
 * Listing summary (thumbnail, price, tier, health, location, type, id) shown in Change Listing Ownership.
 * `compact` shrinks the thumbnail for the bulk modal's list; `extra` renders at the end of the row (e.g. a remove button).
 */
export const ChangeOwnerListingSummary = ({ listingRow, idLabel, idValue, compact = false, extra = null }) => {
  const { t } = useTranslation();
  const { Text } = Typography;

  const listingProductsInfo =
    listingRow?.property?.productsInfo ||
    listingRow?.platforms?.unified?.productsInfo ||
    listingRow?.platforms?.bayut?.productsInfo ||
    listingRow?.platforms?.dubizzle?.productsInfo ||
    {};
  const listingAppliedProductSlug = getAppliedProductSlug(listingProductsInfo);
  const listingTagProps = useMemo(
    () => (listingAppliedProductSlug ? tenantData.getListingActions(listingAppliedProductSlug) : null),
    [listingAppliedProductSlug],
  );

  const locationLine = useMemo(() => {
    if (!listingRow) return '';
    const bc = listingRow?.location?.breadcrumb;
    if (Array.isArray(bc)) {
      return bc
        .filter((e) => e?.level > 1)
        .map((e) => tenantUtils.getLocalisedString(e, 'title'))
        .join(', ');
    }
    return listingRow?.property?.location?.breadcrumb || '';
  }, [listingRow]);

  const listingThumb =
    listingRow?.image?.thumbnail || listingRow?.property?.image?.thumbnail || listingRow?.property?.image?.url;

  const isDailyRentalListing =
    listingRow?.property?.purpose?.slug === 'daily-rental' || listingRow?.listing_purpose?.slug === 'daily-rental';

  return (
    <Flex align={compact ? 'center' : 'flex-start'} gap={'12px'} style={{ width: '100%' }}>
      <div
        style={{
          width: compact ? 72 : 101,
          height: compact ? 60 : 84,
          borderRadius: 8,
          overflow: 'hidden',
          flexShrink: 0,
          lineHeight: 0,
          display: 'block',
          background: '#e8e8e8',
        }}
      >
        <Image src={listingThumb} fallback={`${getBaseURL()}/profolio-assets/images/ph-listings.svg`} {...coverImageInBox} />
      </div>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <Flex align="center" wrap="wrap" gap={'4px'}>
          {listingRow?.property?.price && (
            <span className="fw-700" style={{ lineHeight: 1.2, fontSize: 14 }}>
              <PriceNumber className="text-primary" compact={false} type="price" {...listingRow.property.price} />
              {isDailyRentalListing && (
                <span className="fw-500" style={{ marginInlineStart: 4, fontSize: 11, color: tenantTheme['primary-text-color'] }}>
                  {t('Night')}
                </span>
              )}
            </span>
          )}
          {listingTagProps && listingAppliedProductSlug && (
            <ProductTag {...getUnifiedListingTierProductTagProps(listingAppliedProductSlug, listingTagProps)} />
          )}
          {listingRow?.health?.overall_percentage_score != null && (
            <HealthTag
              className="fw-700 text-center"
              color={listingHealthScoreTagColor(listingRow.health.overall_classification)}
              style={{
                margin: 0,
                fontWeight: 700,
                fontSize: 11,
                lineHeight: '20px',
                padding: '2px 8px',
                width: 'fit-content',
                minWidth: 44,
              }}
            >
              {Math.round(listingRow.health.overall_percentage_score)}%
            </HealthTag>
          )}
        </Flex>
        {!!locationLine && (
          <TextWithIcon
            icon="MdLocationOn"
            gap="6px"
            iconProps={{ size: 12, color: tenantTheme.gray500 }}
            value={locationLine}
            textStyle={{ lineHeight: 1.3, fontSize: 12 }}
          />
        )}
        {(listingRow?.listing_type || listingRow?.listing_purpose) && (
          <Tag
            color={tenantTheme['primary-light-4']}
            style={{
              '--tag-color': tenantTheme['primary-text-color'],
              fontWeight: 700,
              margin: 0,
              width: 'fit-content',
              fontSize: 11,
              lineHeight: 1.3,
              padding: '2px 8px',
            }}
          >
            {listingRow?.listing_type?.title && listingRow?.listing_purpose?.title
              ? `${tenantUtils.getLocalisedString(listingRow.listing_type, 'title')} ${t(`for ${listingRow.listing_purpose.title}`)}`
              : tenantUtils.getLocalisedString(listingRow?.listing_type, 'title') ||
                tenantUtils.getLocalisedString(listingRow?.listing_purpose, 'title') ||
                ''}
          </Tag>
        )}
        <Text type="secondary" style={{ fontSize: 11, lineHeight: 1.3 }}>
          {idLabel} {idValue}
        </Text>
        {!listingRow && (
          <Text type="secondary" style={{ fontSize: 11, lineHeight: 1.3 }}>
            {t('Listing context is limited in this view.')}
          </Text>
        )}
      </div>
      {extra}
    </Flex>
  );
};
